import type { ActivityTrackKey } from "@/core/activity-levels/track-catalog"
import type { MemberRosterState } from "@/core/activity-points/types"

export const guildWarKeys = {
  all: ["guild-war"] as const,
  weeks: () => [...guildWarKeys.all, "weeks"] as const,
  meDashboard: () => [...guildWarKeys.all, "me", "dashboard"] as const,
  candidatePool: (weekId: string) =>
    [...guildWarKeys.all, "candidate-pool", weekId] as const,
  candidatePoolTeamBoard: (weekId: string) =>
    [...guildWarKeys.all, "candidate-pool", "team-board", weekId] as const,
  memberSearch: (query: string) =>
    [...guildWarKeys.all, "member-search", query] as const,
  memberLookup: (memberId: string) =>
    [...guildWarKeys.all, "member-lookup", memberId] as const,
  membersByIds: (memberIds: readonly string[]) =>
    [...guildWarKeys.all, "member-by-ids", memberIds] as const,
  rosterBoard: (rosterDate: string) =>
    [...guildWarKeys.all, "roster-board", rosterDate] as const,
  publishedRoster: (rosterDate: string) =>
    [...guildWarKeys.all, "published-roster", rosterDate] as const,
  publishedRosterMembers: (memberIds: readonly string[]) =>
    [...guildWarKeys.all, "published-roster-members", memberIds] as const,
  permanentMembershipMembers: () =>
    [...guildWarKeys.all, "permanent-membership", "members"] as const,
  pendingPermanentMembershipApplications: () =>
    [
      ...guildWarKeys.all,
      "permanent-membership",
      "applications",
      "pending",
    ] as const,
}

export const talentTrackingKeys = {
  all: ["talent-tracking"] as const,
  overview: () => [...talentTrackingKeys.all, "overview"] as const,
  member: (userId: string) =>
    [...talentTrackingKeys.all, "member", userId] as const,
}

export const talentConfigKeys = {
  all: ["talent-config"] as const,
  snapshot: () => [...talentConfigKeys.all, "snapshot"] as const,
}

export const notificationsKeys = {
  all: ["notifications"] as const,
  inboxBase: () => [...notificationsKeys.all, "inbox"] as const,
  inbox: (input?: { cursor?: string | null; limit?: number }) =>
    [
      ...notificationsKeys.inboxBase(),
      input?.cursor ?? null,
      input?.limit ?? 20,
    ] as const,
}

export const guildEventsKeys = {
  all: ["guild-events"] as const,
  adminList: (input?: {
    status?: string
    bucket?: string
    query?: string
    page?: number
    pageSize?: number
  }) =>
    [
      ...guildEventsKeys.all,
      "admin",
      "list",
      input?.status ?? "all",
      input?.bucket ?? "all",
      input?.query ?? "",
      input?.page ?? null,
      input?.pageSize ?? null,
    ] as const,
  adminDetail: (eventId: string) =>
    [...guildEventsKeys.all, "admin", "detail", eventId] as const,
  schedules: () => [...guildEventsKeys.all, "admin", "schedules"] as const,
  overview: () => [...guildEventsKeys.schedules(), "overview"] as const,
  generationPreview: () =>
    [...guildEventsKeys.schedules(), "generation-preview"] as const,
  memberList: (input?: { state?: string; bucket?: string; page?: number }) =>
    [
      ...guildEventsKeys.all,
      "member",
      "list",
      input?.state ?? "all",
      input?.bucket ?? "all",
      input?.page ?? null,
    ] as const,
  memberDetail: (eventId: string) =>
    [...guildEventsKeys.all, "member", "detail", eventId] as const,
  memberToday: () => [...guildEventsKeys.all, "member", "today"] as const,
}

export const dailyContentKeys = {
  all: ["daily-content"] as const,
  profiles: () => [...dailyContentKeys.all, "profiles"] as const,
}

export const activityLevelsKeys = {
  all: ["activity-levels"] as const,
  tracks: () => [...activityLevelsKeys.all, "tracks"] as const,
  rewards: (activityKey: ActivityTrackKey) =>
    [...activityLevelsKeys.all, "rewards", activityKey] as const,
  progress: (activityKey: ActivityTrackKey, userId: string) =>
    [...activityLevelsKeys.all, "progress", activityKey, userId] as const,
  leaderboard: (activityKey: ActivityTrackKey, limit?: number) =>
    [
      ...activityLevelsKeys.all,
      "leaderboard",
      activityKey,
      limit ?? null,
    ] as const,
  achievementDefinitions: () =>
    [...activityLevelsKeys.all, "achievement-definitions"] as const,
  userAchievements: (userId: string) =>
    [...activityLevelsKeys.all, "user-achievements", userId] as const,
}

export const adminMembersKeys = {
  all: ["admin-members"] as const,
  details: () => [...adminMembersKeys.all, "detail"] as const,
  detail: (memberId: string) =>
    [...adminMembersKeys.details(), memberId] as const,
}

export const titleSuggestionKeys = {
  all: ["title-suggestions"] as const,
  current: () => [...titleSuggestionKeys.all, "current"] as const,
}

export const activityPointsKeys = {
  all: ["activity-points"] as const,
  month: (year: number, month: number, state: MemberRosterState) =>
    [...activityPointsKeys.all, year, month, state] as const,
}

export const birthdaysKeys = {
  all: ["birthdays"] as const,
  config: () => [...birthdaysKeys.all, "config"] as const,
}
